import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  NgZone,
  SimpleChanges,
} from '@angular/core';
import { CatalogCourseTrack, CourseCardStatus } from '../../models';
import { AcademyNavigationService } from '../../../services/academy-navigation.service';

@Component({
  selector: 'ut-academy-track-carousel',
  templateUrl: './academy-track-carousel.component.html',
  styleUrls: ['./academy-track-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AcademyTrackCarouselComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() courseTracks: CatalogCourseTrack[] = [];
  @Input() learningTrackId!: number;
  @Input() enforceOrder = true;
  @Input() isAdmin = false;

  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;

  canScrollLeft = false;
  canScrollRight = false;
  statuses: CourseCardStatus[] = [];

  private resizeObserver?: ResizeObserver;
  private scrollTimerId?: ReturnType<typeof setTimeout>;
  private scrollStateTimerId?: ReturnType<typeof setTimeout>;

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private academyNavigationService: AcademyNavigationService,
  ) {}

  onCourseClick(courseTrackId: number): void {
    this.academyNavigationService.goToCourse(this.learningTrackId, courseTrackId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.courseTracks || changes.enforceOrder || changes.isAdmin) {
      this.statuses = this.computeStatuses();
    }
  }

  ngAfterViewInit(): void {
    this.scrollStateTimerId = setTimeout(() => this.updateScrollState());
    this.scrollToFirstActionable();

    this.ngZone.runOutsideAngular(() => {
      this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll);

      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollState();
      });
      this.resizeObserver.observe(this.scrollContainer.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.scrollContainer?.nativeElement.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    if (this.scrollTimerId != null) clearTimeout(this.scrollTimerId);
    if (this.scrollStateTimerId != null) clearTimeout(this.scrollStateTimerId);
  }

  scrollPrev(): void {
    const el = this.scrollContainer.nativeElement;
    el.scrollBy({ left: -el.clientWidth * 0.8, behavior: 'smooth' });
  }

  scrollNext(): void {
    const el = this.scrollContainer.nativeElement;
    el.scrollBy({ left: el.clientWidth * 0.8, behavior: 'smooth' });
  }

  private computeStatuses(): CourseCardStatus[] {
    let foundIncomplete = false;

    return this.courseTracks.map((ct) => {
      if (ct.user_has_completed_course_track) return 'completed';
      if (this.enforceOrder && !this.isAdmin && foundIncomplete) return 'locked';
      foundIncomplete = true;
      return ct.user_has_started ? 'in-progress' : 'not-started';
    });
  }

  private onScroll = (): void => {
    this.updateScrollState();
  };

  private updateScrollState(): void {
    if (!this.scrollContainer) return;
    const el = this.scrollContainer.nativeElement;
    const prevCanLeft = this.canScrollLeft;
    const prevCanRight = this.canScrollRight;

    this.canScrollLeft = el.scrollLeft > 1;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;

    if (prevCanLeft !== this.canScrollLeft || prevCanRight !== this.canScrollRight) {
      this.ngZone.run(() => this.cdr.markForCheck());
    }
  }

  private scrollToFirstActionable(): void {
    if (!this.statuses.length) return;

    let targetIndex = -1;

    for (let i = 0; i < this.statuses.length; i++) {
      if (this.statuses[i] === 'in-progress') {
        targetIndex = i;
        break;
      }
      if (this.statuses[i] === 'not-started' && targetIndex === -1) {
        targetIndex = i;
      }
    }

    if (targetIndex > 0) {
      this.scrollTimerId = setTimeout(() => {
        const cards = this.scrollContainer.nativeElement.children;
        if (cards[targetIndex]) {
          const card = cards[targetIndex] as HTMLElement;
          this.scrollContainer.nativeElement.scrollLeft = card.offsetLeft;
        }
      });
    }
  }
}
